import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { DEFAULT_SOUND, isOpenerVariant, type AnimationConfig } from '@game-platform/pack-animation';
import { Button } from '../design-system/Button';
import { Bolt, Speaker } from '../design-system';
import { useOpeningAnimation } from '../openingAnimation';
import { setMuted, useMuted } from './soundSettings';
import { skipAnimation, useSkipAnimation } from './playerPreference';

/**
 * The switch's face when it is on: the buy CTA's own fill, text and glow —
 * the very variables the primary button reads, fallbacks included — so the
 * bolt up here and the bolt in the CTA below are one colour under any
 * operator theme, and an active skip reads at a glance. Off, the glass stays
 * as Exit's and the glyph dims.
 */
const ON_CLASSES =
  '[background:var(--co-Button-primaryFill,var(--ui-button))] ' +
  '[border-color:var(--co-Button-primaryBorder,transparent)] ' +
  '[color:var(--co-Button-primaryText,var(--ui-button-text))] ' +
  '[box-shadow:var(--co-Button-primaryShadow,var(--shadow-button-gloss))]';
const OFF_CLASSES = '[&>svg]:opacity-70';

/** Round, icon-only, on the frosted glass every top-bar control shares. */
const ROUND = 'w-9 px-0 rounded-full';

/** The mute's slot is this wide with its gap; the bolt travels exactly that. */
const MUTE_SLOT = { width: 36, gap: 8 };

/**
 * The mute folding away is one tween of its slot's width: the bolt, in the
 * flow before it, rides that width to the mute's place with no motion of its
 * own — one curve, nothing to land twice. (A layout animation on the bolt
 * as well would race the slot's removal and hop at the end.)
 */
const FOLD = { duration: 0.32, ease: [0.4, 0, 0.2, 1] } as const;

export interface RevealTogglesProps {
  /** The pack's own opening mechanic; absent or null: the operator's. */
  animation?: AnimationConfig | null;
}

/**
 * The player's two switches in the reveal's top bar: skip the animation, and
 * mute its sound. Both are off until the player says otherwise and keep
 * their state across sessions. The skip exists for every mechanic; the mute
 * only for a library one, only while the operator has sound on — and only
 * while the animation plays: a skipped opening has no ceremony to score, so
 * switching the skip on folds the mute away and the bolt slides into its
 * place, and switching it off brings the mute back the same way.
 */
export function RevealToggles({ animation: override }: RevealTogglesProps) {
  const animation = useOpeningAnimation(override);
  const skipping = useSkipAnimation();
  const muted = useMuted();
  const reduceMotion = useReducedMotion();

  const showMute =
    isOpenerVariant(animation.variant) &&
    (animation.sound ?? DEFAULT_SOUND).enabled &&
    !skipping;

  return (
    <div className="flex items-center">
      <Button
        variant="glass-frosted"
        size="md"
        aria-label="Skip animation"
        aria-pressed={skipping}
        title={skipping ? 'Skip animation: on' : 'Skip animation: off'}
        className={`${ROUND} ${skipping ? ON_CLASSES : OFF_CLASSES}`}
        onClick={() => skipAnimation.set(!skipping)}
      >
        <Bolt size={18} fill={skipping ? 'currentColor' : 'none'} />
      </Button>
      <AnimatePresence initial={false}>
        {showMute && (
          <motion.div
            key="mute"
            className="flex items-center justify-end overflow-hidden"
            initial={reduceMotion ? false : { width: 0, opacity: 0 }}
            animate={{ width: MUTE_SLOT.width + MUTE_SLOT.gap, opacity: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { width: 0, opacity: 0 }}
            transition={reduceMotion ? { duration: 0 } : FOLD}
          >
            <motion.div
              initial={reduceMotion ? false : { scale: 0.6 }}
              animate={{ scale: 1 }}
              exit={reduceMotion ? {} : { scale: 0.6 }}
              transition={reduceMotion ? { duration: 0 } : FOLD}
            >
              <Button
                variant="glass-frosted"
                size="md"
                aria-label="Sound"
                aria-pressed={!muted}
                title={muted ? 'Sound: off' : 'Sound: on'}
                className={`${ROUND} ${muted ? OFF_CLASSES : ''}`}
                onClick={() => setMuted(!muted)}
              >
                <Speaker size={18} muted={muted} />
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
